import { useTypingConfig } from 'Hooks/config/useMessagingConfig';
import { useCallback, useEffect, useRef } from 'react';
import { useUpdateTypingState } from '../../requests/index.requests';
import {
  UseTypingIndicatorSignalOptions,
  UseTypingIndicatorSignalResult,
} from './index.types';

type TypingTimers = {
  isTyping: boolean;
  refreshTimer: ReturnType<typeof setInterval> | null;
  idleTimer: ReturnType<typeof setTimeout> | null;
};

const clearTimers = (timers: TypingTimers) => {
  if (timers.refreshTimer !== null) {
    clearInterval(timers.refreshTimer);
    timers.refreshTimer = null;
  }

  if (timers.idleTimer !== null) {
    clearTimeout(timers.idleTimer);
    timers.idleTimer = null;
  }
};

const restartIdleTimer = (
  timers: TypingTimers,
  idleStopMs: number,
  onIdle: () => void
) => {
  if (timers.idleTimer !== null) {
    clearTimeout(timers.idleTimer);
    timers.idleTimer = null;
  }

  timers.idleTimer = setTimeout(onIdle, idleStopMs);
};

/**
 * Sends `typing` while the input has content, refreshing on an interval,
 * and `stopped` when the input is cleared, the user goes idle, signaling
 * is turned off, the conversation changes, or the hook unmounts.
 *
 * Timing comes from the messaging config. Nothing is sent until that
 * config is available.
 */
export const useTypingIndicatorSignal = ({
  conversationId,
  enabled = true,
}: UseTypingIndicatorSignalOptions): UseTypingIndicatorSignalResult => {
  const { mutate } = useUpdateTypingState();
  const typingConfig = useTypingConfig();

  const canSignal = Boolean(enabled && conversationId && typingConfig);
  const refreshIntervalMs = typingConfig?.refreshIntervalMs ?? 0;
  const idleStopMs = typingConfig?.idleStopMs ?? 0;

  const timersRef = useRef<TypingTimers>({
    isTyping: false,
    refreshTimer: null,
    idleTimer: null,
  });

  // Clears the timers and updates the typing state to stopped
  const stop = useCallback(
    (conversationId?: string | null) => {
      const timers = timersRef.current;
      clearTimers(timers);

      if (!timers.isTyping) {
        return;
      }

      timers.isTyping = false;

      // Typing can only start with a conversation id; this guards callers that
      // clear the id before invoking stop.
      if (conversationId) {
        mutate({ conversationId, state: 'stopped' });
      }
    },
    [mutate]
  );

  const onInputValueChange = useCallback(
    (value: string) => {
      if (
        !canSignal ||
        !conversationId ||
        refreshIntervalMs <= 0 ||
        idleStopMs <= 0
      ) {
        return;
      }

      // If the user clears the input, stop typing
      if (value.trim().length === 0) {
        stop(conversationId);
        return;
      }

      // If the user was not typing, start typing and set the refresh timer
      if (!timersRef.current.isTyping) {
        timersRef.current.isTyping = true;
        mutate({ conversationId, state: 'typing' });

        timersRef.current.refreshTimer = setInterval(() => {
          if (!timersRef.current.isTyping) {
            return;
          }

          mutate({ conversationId, state: 'typing' });
        }, refreshIntervalMs);
      }

      restartIdleTimer(timersRef.current, idleStopMs, () => {
        stop(conversationId);
      });
    },
    [canSignal, conversationId, idleStopMs, mutate, refreshIntervalMs, stop]
  );

  const stopTyping = useCallback(() => {
    stop(conversationId);
  }, [conversationId, stop]);

  // Cleanup closes over this run's conversation. Changing conversations or
  // turning signaling off re-runs the effect, and that cleanup stops the
  // conversation that was actually active.
  useEffect(() => {
    return () => {
      if (canSignal) {
        stop(conversationId);
      }
    };
  }, [canSignal, conversationId, stop]);

  return {
    onInputValueChange,
    stopTyping,
  };
};
